import { pdf } from '@react-pdf/renderer';
import React from 'react';
import {
  Document,
  Image,
  Page,
  Path,
  Svg,
  StyleSheet,
  Text,
  View,
} from '@react-pdf/renderer';
import type { HandlingsCoachV2Section } from 'src/contracts/oth';

import logoImage from 'src/assets/images/logos/logo-with-text_dark-148.png';

export type AnalysisPdfBlock =
  | { kind: 'section'; section: HandlingsCoachV2Section }
  | {
      kind: 'missing';
      boxId: string;
      category: string;
      field: string;
      message: string;
      user_input?: { now?: string; desired?: string; action?: string };
    };

/* Farger tilpasset UI: Første steg-bokser og vurdering */
const førsteStegColors = [
  { bg: '#E8F0FE', border: '#B3D4FC' }, // lys blå
  { bg: '#E6F7ED', border: '#A7F3D0' }, // lys grønn
  { bg: '#FFF8E5', border: '#FDE68A' }, // lys oransje/gul
];

const styles = StyleSheet.create({
  page: {
    padding: 36,
    paddingBottom: 64,
    fontSize: 11,
    backgroundColor: '#F9FAFB',
  },
  header: { marginBottom: 24 },
  title: {
    fontSize: 20,
    fontWeight: 700,
    color: '#111827',
  },
  meta: { marginTop: 10, color: '#6B7280', fontSize: 10 },

  section: { marginTop: 18 },
  sectionTitle: {
    fontSize: 14,
    fontWeight: 700,
    marginBottom: 8,
    color: '#111827',
  },
  body: {
    fontSize: 11,
    lineHeight: 1.5,
    marginBottom: 8,
    color: '#374151',
  },

  block: {
    borderWidth: 1,
    borderColor: '#E5E7EB',
    borderRadius: 8,
    padding: 16,
    marginBottom: 20,
    backgroundColor: '#FFFFFF',
  },
  blockTitle: {
    fontSize: 15,
    fontWeight: 700,
    marginBottom: 12,
    color: '#111827',
  },
  missingText: { fontSize: 11, color: '#6B7280', fontStyle: 'italic' },
  subTitle: {
    fontSize: 12,
    fontWeight: 700,
    marginTop: 14,
    marginBottom: 8,
    color: '#111827',
  },
  duSkrevLabel: { fontSize: 11, fontWeight: 700, color: '#111827' },
  duSkrevValue: { fontSize: 11, color: '#374151' },
  listItem: {
    marginLeft: 14,
    marginBottom: 4,
    fontSize: 11,
    color: '#4B5563',
  },
  listLabel: {
    fontSize: 11,
    fontWeight: 700,
    marginBottom: 4,
    color: '#111827',
  },
  tiltakCard: {
    backgroundColor: '#FFFFFF',
    borderWidth: 1,
    borderColor: '#E5E7EB',
    borderRadius: 6,
    padding: 12,
    marginBottom: 14,
  },
  tiltakCardTitle: {
    fontSize: 12,
    fontWeight: 700,
    marginBottom: 6,
    color: '#111827',
  },
  forslagBox: {
    backgroundColor: '#ECFEFF',
    borderWidth: 1,
    borderColor: '#A5F3FC',
    borderRadius: 6,
    padding: 10,
    marginTop: 8,
    marginBottom: 8,
  },
  nesteStegBox: {
    backgroundColor: '#FEFCE8',
    borderWidth: 1,
    borderColor: '#FEF08A',
    borderRadius: 6,
    padding: 10,
    marginTop: 10,
  },
  nesteStegLabel: { fontSize: 11, fontWeight: 700, color: '#111827' },
  lockedRow: { flexDirection: 'row', alignItems: 'flex-start' },
  lockedIcon: { width: 16, marginRight: 8 },
  lockedIconText: { fontSize: 12, color: '#b45309' },
  lockedTextWrap: { flex: 1 },

  footer: {
    position: 'absolute',
    left: 36,
    right: 36,
    bottom: 18,
    fontSize: 9,
    color: '#6B7280',
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
  },
  footerLeft: {
    flex: 1,
    display: 'flex',
    flexDirection: 'row',
    justifyContent: 'flex-start',
    alignItems: 'center',
  },
  footerLogoWrap: {
    flex: 1,
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
  },
  footerRight: {
    flex: 1,
    display: 'flex',
    flexDirection: 'row',
    justifyContent: 'flex-end',
    alignItems: 'center',
  },
  footerLogo: {
    width: 80,
    height: 24,
    objectFit: 'contain',
  },
});

const formatDateTime = (d: Date) =>
  `${d.toLocaleDateString('no-NO')} ${d.toLocaleTimeString('no-NO', {
    hour: '2-digit',
    minute: '2-digit',
  })}`;

const WarningIcon: React.FC<{ size?: number; color?: string }> = ({
  size = 12,
  color = '#b45309',
}) => (
  <Svg width={size} height={size} viewBox="0 0 24 24">
    <Path d="M1 21h22L12 2 1 21zm12-3h-2v-2h2v2zm0-4h-2v-4h2v4z" fill={color} />
  </Svg>
);

export async function downloadAnalysisPdf(input: {
  intro: string;
  blocks: AnalysisPdfBlock[];
  exportedAt?: Date;
}) {
  const { intro, blocks, exportedAt = new Date() } = input;

  const doc = (
    <Document>
      <Page size="A4" style={styles.page} wrap>
        <View style={styles.header}>
          <Text style={styles.title}>Tiltak forslag – Analyse</Text>
          <Text style={styles.meta}>
            Eksportert: {formatDateTime(exportedAt)}
          </Text>
        </View>

        {intro && (
          <View style={styles.section}>
            <Text style={styles.body}>{intro}</Text>
          </View>
        )}

        {blocks.map((b, idx) => {
          if (b.kind === 'missing') {
            const now = b.user_input?.now?.trim() ?? '';
            const desired = b.user_input?.desired?.trim() ?? '';
            const action = b.user_input?.action?.trim() ?? '';
            const hasAnyInput = Boolean(now || desired || action);
            const missingNow = now.length === 0;
            const missingDesired = desired.length === 0;
            const hasMissing = missingNow || missingDesired;

            const aiDisabledText =
              missingNow && missingDesired
                ? 'Fyll ut Nå-tilstand og Ønsket tilstand (samme felt) for å få foreslåtte tiltak.'
                : missingDesired
                  ? 'Ønsket tilstand mangler. Fyll ut Ønsket tilstand i fanen “Ønsket tilstand” for å få foreslåtte tiltak.'
                  : 'Nå-tilstand mangler. Fyll ut Nå-tilstand i fanen “Nå-tilstand” for å få foreslåtte tiltak.';
            return (
              <View key={b.boxId ?? idx} style={styles.block} wrap>
                <Text style={styles.blockTitle}>
                  {b.category} – {b.field}
                </Text>

                {hasAnyInput ? (
                  <>
                    <View style={{ marginBottom: 12 }}>
                      <Text style={styles.subTitle}>Du skrev</Text>
                      <Text style={styles.duSkrevValue}>
                        <Text style={styles.duSkrevLabel}>Nå-tilstand: </Text>
                        {(now || 'Mangler') + '\n'}
                        <Text style={styles.duSkrevLabel}>
                          Ønsket tilstand:{' '}
                        </Text>
                        {(desired || 'Mangler') + '\n'}
                        <Text style={styles.duSkrevLabel}>Tiltak: </Text>
                        {action || 'Mangler'}
                      </Text>
                    </View>

                    {hasMissing ? (
                      <View style={styles.nesteStegBox}>
                        <View style={styles.lockedRow}>
                          <View style={styles.lockedIcon}>
                            <WarningIcon />
                          </View>
                          <View style={styles.lockedTextWrap}>
                            <Text style={styles.body}>
                              <Text style={styles.nesteStegLabel}>
                                Foreslåtte tiltak:{' '}
                              </Text>
                              {aiDisabledText}
                            </Text>
                          </View>
                        </View>
                      </View>
                    ) : (
                      <Text style={styles.missingText}>{b.message}</Text>
                    )}
                  </>
                ) : (
                  <Text style={styles.missingText}>{b.message}</Text>
                )}
              </View>
            );
          }

          const section = b.section;
          return (
            <View key={section.box_id ?? idx} style={styles.block} wrap>
              <Text style={styles.blockTitle}>
                {section.category} – {section.field}
              </Text>

              <View style={{ marginBottom: 12 }}>
                <Text style={styles.subTitle}>Du skrev</Text>
                <Text style={styles.body}>
                  Nå-tilstand:{' '}
                  {(section.user_input?.now?.trim() || 'Mangler') + '\n'}
                  Ønsket tilstand:{' '}
                  {(section.user_input?.desired?.trim() || 'Mangler') + '\n'}
                  Tiltak: {section.user_input?.action?.trim() || 'Mangler'}
                </Text>
              </View>

              {Array.isArray(section.proposed_actions) &&
                section.proposed_actions.length > 0 && (
                  <>
                    <Text style={styles.subTitle}>Foreslåtte tiltak</Text>
                    {section.proposed_actions.map((pa, i) => {
                      const farge =
                        førsteStegColors[i % førsteStegColors.length];
                      return (
                        <View key={i} style={styles.tiltakCard}>
                          <Text style={styles.tiltakCardTitle}>{pa.title}</Text>
                          <Text style={styles.body}>{pa.description}</Text>
                          {pa.first_step && (
                            <View
                              style={{
                                backgroundColor: farge.bg,
                                borderWidth: 1,
                                borderColor: farge.border,
                                borderRadius: 4,
                                padding: 8,
                                marginTop: 6,
                              }}
                            >
                              <Text
                                style={{
                                  fontSize: 10,
                                  color: '#374151',
                                }}
                              >
                                <Text style={{ fontWeight: 700 }}>
                                  Første steg:{' '}
                                </Text>
                                → {pa.first_step}
                              </Text>
                            </View>
                          )}
                        </View>
                      );
                    })}
                  </>
                )}

              {section.user_input?.action && section.review && (
                <>
                  <Text style={styles.subTitle}>Vurdering av ditt tiltak</Text>
                  {section.review.likes?.length > 0 && (
                    <View style={{ marginBottom: 10 }}>
                      <Text style={styles.listLabel}>Styrker:</Text>
                      {section.review.likes.map((l, i) => (
                        <Text key={i} style={styles.listItem}>
                          • {l}
                        </Text>
                      ))}
                    </View>
                  )}
                  {section.review.concerns?.length > 0 && (
                    <View style={{ marginBottom: 10 }}>
                      <Text style={styles.listLabel}>Bekymringer:</Text>
                      {section.review.concerns.map((c, i) => (
                        <Text key={i} style={styles.listItem}>
                          • {c}
                        </Text>
                      ))}
                    </View>
                  )}
                  {Array.isArray(section.review.questions) &&
                    section.review.questions.length > 0 && (
                      <View style={{ marginBottom: 10 }}>
                        <Text style={styles.listLabel}>Spørsmål:</Text>
                        {section.review.questions.map((q, i) => (
                          <Text key={i} style={styles.listItem}>
                            • {q}
                          </Text>
                        ))}
                      </View>
                    )}
                  {section.review.suggested_rewrite && (
                    <View style={styles.forslagBox}>
                      <Text style={[styles.body, { marginBottom: 0 }]}>
                        <Text style={{ fontWeight: 700 }}>
                          Forslag til forbedring:{' '}
                        </Text>
                        {section.review.suggested_rewrite}
                      </Text>
                    </View>
                  )}
                </>
              )}

              {section.next_step && (
                <View style={styles.nesteStegBox}>
                  <Text style={styles.nesteStegLabel}>Neste steg: </Text>
                  <Text style={[styles.body, { marginBottom: 0 }]}>
                    {section.next_step}
                  </Text>
                </View>
              )}
            </View>
          );
        })}

        <View style={styles.footer} fixed>
          <View style={styles.footerLeft}>
            <Text>Tiltak forslag – Analyse</Text>
          </View>
          <View style={styles.footerLogoWrap}>
            <Image src={logoImage} style={styles.footerLogo} />
          </View>
          <View style={styles.footerRight}>
            <Text
              render={({ pageNumber, totalPages }) =>
                `Side ${pageNumber} av ${totalPages}`
              }
            />
          </View>
        </View>
      </Page>
    </Document>
  );

  const blob = await pdf(doc as React.ReactElement).toBlob();
  const stamp = new Date().toISOString().slice(0, 19).replace(/[:T]/g, '-');
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = `tiltak-forslag-analyse-${stamp}.pdf`;
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
}
